/**
 * Unified MA-* error code catalog for all user-facing webapp surfaces.
 * Re-exports connection codes and adds domain-specific codes.
 */

import { connectionErrorHint, type MiniappConnectionErrorCode } from '../miniapp/utils/miniappConnectionErrorCodes';

export {
  MINIAPP_CONNECTION_ERROR_CODES as MA_CONN_CODES,
  type MiniappConnectionErrorCode as MaConnCode,
  connectionErrorHint,
  resolveConnectionErrorPresentation,
  type ConnectionErrorPresentation,
} from '../miniapp/utils/miniappConnectionErrorCodes';

export const MA_AUTH_CODES = {
  SESSION_EXPIRED: 'MA-AUTH-2001',
  INVALID_SIGNATURE: 'MA-AUTH-2002',
  USE_DELEGATED_API: 'MA-AUTH-2003',
  WRONG_DEPLOYMENT: 'MA-AUTH-2004',
  UNAUTHORIZED: 'MA-AUTH-2005',
  UNKNOWN: 'MA-AUTH-2999',
} as const;

export const MA_TRADE_CODES = {
  INVALID_PAIR: 'MA-TRADE-3001',
  EXECUTION_FAILED: 'MA-TRADE-3002',
  CLOSE_FAILED: 'MA-TRADE-3003',
  VALIDATION_FAILED: 'MA-TRADE-3004',
  PRICE_UNAVAILABLE: 'MA-TRADE-3005',
  SL_INVALID: 'MA-TRADE-3006',
  MARGIN_INSUFFICIENT: 'MA-TRADE-3007',
  WALLET_CONFIG: 'MA-TRADE-3008',
  ORDER_REJECTED: 'MA-TRADE-3009',
  SIZE_INVALID: 'MA-TRADE-3010',
  TRADING_HALTED: 'MA-TRADE-3011',
  UNKNOWN: 'MA-TRADE-3999',
} as const;

export const MA_STRAT_CODES = {
  NOT_FOUND: 'MA-STRAT-4001',
  QUEUE_FULL: 'MA-STRAT-4002',
  OPTIMIZE_FAILED: 'MA-STRAT-4003',
  BACKTEST_FAILED: 'MA-STRAT-4004',
  CLONE_FAILED: 'MA-STRAT-4005',
  UNKNOWN: 'MA-STRAT-4999',
} as const;

export const MA_AGENT_CODES = {
  TIER_BLOCKED: 'MA-AGENT-5001',
  PROVIDER_TIMEOUT: 'MA-AGENT-5002',
  CHAT_FAILED: 'MA-AGENT-5003',
  TOOL_FAILED: 'MA-AGENT-5004',
  PROVIDER_AUTH: 'MA-AGENT-5005',
  SETTINGS_FAILED: 'MA-AGENT-5006',
  SCHEDULE_FAILED: 'MA-AGENT-5007',
  QUICK_START_FAILED: 'MA-AGENT-5008',
  PROVIDER_TEST_FAILED: 'MA-AGENT-5009',
  SUBSCRIBE_FAILED: 'MA-AGENT-5010',
  NO_PROVIDER_KEY: 'MA-AGENT-5011',
  NO_CACHED_PARAMS: 'MA-AGENT-5012',
  SCHEDULE_LIMIT: 'MA-AGENT-5013',
  DEPS_NOT_READY: 'MA-AGENT-5014',
  CONCURRENT_TURN: 'MA-AGENT-5015',
  ROSTER_GUARD: 'MA-AGENT-5016',
  STORAGE_NOT_CONFIGURED: 'MA-AGENT-5017',
  ENCRYPTION_NOT_CONFIGURED: 'MA-AGENT-5018',
  CONVERSATION_FAILED: 'MA-AGENT-5019',
  PROVIDER_NETWORK: 'MA-AGENT-5020',
  ACCOUNT_NOT_READY: 'MA-AGENT-5021',
  UNKNOWN: 'MA-AGENT-5999',
} as const;

export const MA_WALLET_CODES = {
  ADDRESS_INVALID: 'MA-WALLET-6001',
  LOAD_FAILED: 'MA-WALLET-6002',
  UNKNOWN: 'MA-WALLET-6999',
} as const;

export const MA_RATE_CODES = {
  RATE_LIMITED: 'MA-RATE-7001',
  UNKNOWN: 'MA-RATE-7999',
} as const;

export const MA_SVC_CODES = {
  UNAVAILABLE: 'MA-SVC-8001',
  UNKNOWN: 'MA-SVC-8999',
} as const;

export const MA_UI_CODES = {
  CHUNK_LOAD_FAILED: 'MA-UI-9001',
  BOUNDARY_FAILED: 'MA-UI-9002',
  RENDER_FAILED: 'MA-UI-9003',
  UNKNOWN: 'MA-UI-9999',
} as const;

export const MA_OBS_CODES = {
  PARITY_TIMEOUT: 'MA-OBS-0001',
  OPTIMIZER_FAILED: 'MA-OBS-0002',
  CHART_LOAD_FAILED: 'MA-OBS-0003',
  ML_INFERENCE_FAILED: 'MA-OBS-0004',
  LOAD_FAILED: 'MA-OBS-0005',
  UNKNOWN: 'MA-OBS-0999',
} as const;

export type MaErrorCode =
  | import('../miniapp/utils/miniappConnectionErrorCodes').MiniappConnectionErrorCode
  | (typeof MA_AUTH_CODES)[keyof typeof MA_AUTH_CODES]
  | (typeof MA_TRADE_CODES)[keyof typeof MA_TRADE_CODES]
  | (typeof MA_STRAT_CODES)[keyof typeof MA_STRAT_CODES]
  | (typeof MA_AGENT_CODES)[keyof typeof MA_AGENT_CODES]
  | (typeof MA_WALLET_CODES)[keyof typeof MA_WALLET_CODES]
  | (typeof MA_RATE_CODES)[keyof typeof MA_RATE_CODES]
  | (typeof MA_SVC_CODES)[keyof typeof MA_SVC_CODES]
  | (typeof MA_UI_CODES)[keyof typeof MA_UI_CODES]
  | (typeof MA_OBS_CODES)[keyof typeof MA_OBS_CODES];

const MA_HINTS: Record<string, string> = {
  // AUTH
  'MA-AUTH-2001': 'Session expired — sign in again.',
  'MA-AUTH-2002': 'Telegram authentication signature invalid.',
  'MA-AUTH-2003': 'Use the delegated API for headless access.',
  'MA-AUTH-2004': 'Wrong bot deployment for this account.',
  'MA-AUTH-2005': 'Authentication required.',
  'MA-AUTH-2999': 'Authentication failed.',
  // TRADE
  'MA-TRADE-3001': 'Invalid trading pair.',
  'MA-TRADE-3002': 'Trade execution failed.',
  'MA-TRADE-3003': 'Position close failed.',
  'MA-TRADE-3004': 'Trade validation failed.',
  'MA-TRADE-3005': 'Real-time price unavailable for this pair.',
  'MA-TRADE-3006': 'Stop loss is invalid or beyond liquidation.',
  'MA-TRADE-3007': 'Insufficient margin for this trade.',
  'MA-TRADE-3008': 'Wallet or API configuration issue.',
  'MA-TRADE-3009': 'Order was rejected by the exchange.',
  'MA-TRADE-3010': 'Position size is too small or invalid.',
  'MA-TRADE-3011': 'Trading is halted for this market.',
  'MA-TRADE-3999': 'Trade operation failed.',
  // STRAT
  'MA-STRAT-4001': 'Strategy not found.',
  'MA-STRAT-4002': 'Backtest queue is full — try again shortly.',
  'MA-STRAT-4003': 'Optimization failed.',
  'MA-STRAT-4004': 'Backtest failed.',
  'MA-STRAT-4005': 'Strategy clone failed.',
  'MA-STRAT-4999': 'Strategy operation failed.',
  // AGENT
  'MA-AGENT-5001': 'Feature blocked by your tier.',
  'MA-AGENT-5002': 'AI provider timed out.',
  'MA-AGENT-5003': 'Agent chat failed.',
  'MA-AGENT-5004': 'Agent tool execution failed.',
  'MA-AGENT-5005': 'AI provider authentication failed.',
  'MA-AGENT-5006': 'Agent settings could not be saved or loaded.',
  'MA-AGENT-5007': 'Scheduled agent prompt failed.',
  'MA-AGENT-5008': 'Agent quick start failed.',
  'MA-AGENT-5009': 'AI provider connectivity test failed.',
  'MA-AGENT-5010': 'Strategy subscribe failed.',
  'MA-AGENT-5011': 'No Agent provider key configured.',
  'MA-AGENT-5012': 'No cached optimized parameters available.',
  'MA-AGENT-5013': 'Scheduled prompt limit reached.',
  'MA-AGENT-5014': 'Agent services are still starting — retry shortly.',
  'MA-AGENT-5015': 'Another agent turn is already running.',
  'MA-AGENT-5016': 'Daily pick roster allows only one change per run.',
  'MA-AGENT-5017': 'Agent key storage is not configured on this server.',
  'MA-AGENT-5018': 'Agent key encryption is not configured on this server.',
  'MA-AGENT-5019': 'Agent conversation storage failed.',
  'MA-AGENT-5020': 'Could not reach the AI provider network.',
  'MA-AGENT-5021': 'Account is not ready for automation (wallet / fee approval).',
  'MA-AGENT-5999': 'Agent operation failed.',
  // WALLET
  'MA-WALLET-6001': 'Invalid wallet address.',
  'MA-WALLET-6002': 'Wallet data could not be loaded.',
  'MA-WALLET-6999': 'Wallet operation failed.',
  // RATE
  'MA-RATE-7001': 'Too many requests — wait and retry.',
  'MA-RATE-7999': 'Rate limit exceeded.',
  // SVC
  'MA-SVC-8001': 'Service temporarily unavailable.',
  'MA-SVC-8999': 'Service error.',
  // UI
  'MA-UI-9001': 'App assets failed to load — reload the page.',
  'MA-UI-9002': 'This screen failed to render.',
  'MA-UI-9003': 'Unexpected render error.',
  'MA-UI-9999': 'Client error.',
  // OBS
  'MA-OBS-0001': 'Parity check timed out.',
  'MA-OBS-0002': 'Batch optimizer failed.',
  'MA-OBS-0003': 'Chart data failed to load.',
  'MA-OBS-0004': 'ML inference unavailable.',
  'MA-OBS-0005': 'Observatory data failed to load.',
  'MA-OBS-0999': 'Observatory operation failed.',
};

/** Map backend snake_case / AppError codes → MA-* */
const API_CODE_TO_MA: Record<string, MaErrorCode> = {
  auth_missing: MA_AUTH_CODES.UNAUTHORIZED,
  auth_invalid: MA_AUTH_CODES.INVALID_SIGNATURE,
  global_key: MA_AUTH_CODES.UNAUTHORIZED,
  lookup_failed: MA_AUTH_CODES.UNKNOWN,
  use_delegated_api: MA_AUTH_CODES.USE_DELEGATED_API,
  frontend_tenant_required: MA_AUTH_CODES.UNAUTHORIZED,
  frontend_tenant_invalid: MA_AUTH_CODES.WRONG_DEPLOYMENT,
  rate_limited: MA_RATE_CODES.RATE_LIMITED,
  QUEUE_FULL: MA_STRAT_CODES.QUEUE_FULL,
  INSTANCE_QUEUE_FULL: MA_STRAT_CODES.QUEUE_FULL,
  strategy_not_found: MA_STRAT_CODES.NOT_FOUND,
  not_found: MA_STRAT_CODES.NOT_FOUND,
  tier_l0: MA_AGENT_CODES.TIER_BLOCKED,
  tier_blocked: MA_AGENT_CODES.TIER_BLOCKED,
  daily_top_pick_tool_blocked: MA_AGENT_CODES.TIER_BLOCKED,
  key_scope: MA_AUTH_CODES.UNAUTHORIZED,
  pack_denied: MA_AGENT_CODES.TIER_BLOCKED,
  quota_exceeded: MA_RATE_CODES.RATE_LIMITED,
  mutate_tools_disabled: MA_AGENT_CODES.TIER_BLOCKED,
  delegated_staged_trade_only: MA_AGENT_CODES.TIER_BLOCKED,
  delegated_mcp_disabled: MA_SVC_CODES.UNAVAILABLE,
  positions_unavailable: MA_SVC_CODES.UNAVAILABLE,
  pnl_unavailable: MA_SVC_CODES.UNAVAILABLE,
  BT_NO_CANDLES: MA_STRAT_CODES.BACKTEST_FAILED,
  BT_INSUFFICIENT_CANDLES: MA_STRAT_CODES.BACKTEST_FAILED,
  BT_INVALID_INPUT: MA_STRAT_CODES.BACKTEST_FAILED,
  redis_not_configured: MA_SVC_CODES.UNAVAILABLE,
  skipped_no_key: MA_AGENT_CODES.NO_PROVIDER_KEY,
  mcp_settings_failed: MA_AGENT_CODES.SETTINGS_FAILED,
  mcp_schedule_failed: MA_AGENT_CODES.SCHEDULE_FAILED,
  mcp_quick_start_failed: MA_AGENT_CODES.QUICK_START_FAILED,
  mcp_provider_list_failed: MA_AGENT_CODES.PROVIDER_TEST_FAILED,
  mcp_provider_upsert_failed: MA_AGENT_CODES.PROVIDER_AUTH,
  mcp_provider_delete_failed: MA_AGENT_CODES.SETTINGS_FAILED,
  provider_test_failed: MA_AGENT_CODES.PROVIDER_TEST_FAILED,
  mcp_provider_models_failed: MA_AGENT_CODES.PROVIDER_TEST_FAILED,
  chat_failed: MA_AGENT_CODES.CHAT_FAILED,
  mcp_chat_stream_failed: MA_AGENT_CODES.CHAT_FAILED,
  mcp_cli_failed: MA_AGENT_CODES.CHAT_FAILED,
  mcp_warm_failed: MA_AGENT_CODES.CHAT_FAILED,
  mcp_builder_snapshot_failed: MA_AGENT_CODES.TOOL_FAILED,
  mcp_conversation_failed: MA_AGENT_CODES.CONVERSATION_FAILED,
  subscribe_failed: MA_AGENT_CODES.SUBSCRIBE_FAILED,
  daily_top_pick_cached_params_required: MA_AGENT_CODES.NO_CACHED_PARAMS,
  account_not_ready: MA_AGENT_CODES.ACCOUNT_NOT_READY,
  daily_top_pick_roster_guard: MA_AGENT_CODES.ROSTER_GUARD,
  daily_top_pick_manual_stack: MA_AGENT_CODES.ROSTER_GUARD,
  schedule_limit_reached: MA_AGENT_CODES.SCHEDULE_LIMIT,
  mcp_deps_not_initialized: MA_AGENT_CODES.DEPS_NOT_READY,
  concurrent_turn: MA_AGENT_CODES.CONCURRENT_TURN,
  tool_failed: MA_AGENT_CODES.TOOL_FAILED,
  mcp_storage_not_configured: MA_AGENT_CODES.STORAGE_NOT_CONFIGURED,
  mcp_encryption_not_configured: MA_AGENT_CODES.ENCRYPTION_NOT_CONFIGURED,
};

export function maErrorHint(code: string | undefined | null): string {
  if (!code) return 'An error occurred.';
  if (code.startsWith('MA-CONN-')) {
    return connectionErrorHint(code as MiniappConnectionErrorCode);
  }
  return MA_HINTS[code] ?? 'An error occurred. Share this code with support.';
}

export type ApiErrorPayload = {
  success?: boolean;
  error?: string;
  message?: string;
  code?: string;
  maCode?: string;
  retryAfterSec?: number;
};

export function resolveMaErrorFromApi(
  json: ApiErrorPayload | null | undefined,
  httpStatus?: number,
  fallbackMessage?: string,
): { maCode: MaErrorCode; message: string } {
  if (json?.maCode && String(json.maCode).startsWith('MA-')) {
    return {
      maCode: json.maCode as MaErrorCode,
      message: String(json.message || json.error || fallbackMessage || 'Request failed'),
    };
  }
  const apiCode = String(json?.code || '').trim();
  if (apiCode && API_CODE_TO_MA[apiCode]) {
    return {
      maCode: API_CODE_TO_MA[apiCode],
      message: String(json?.message || json?.error || fallbackMessage || 'Request failed'),
    };
  }
  const msg = String(json?.message || json?.error || fallbackMessage || '').trim();
  const msgLower = msg.toLowerCase();

  if (httpStatus === 429 || apiCode === 'rate_limited' || msgLower.includes('rate limit')) {
    return { maCode: MA_RATE_CODES.RATE_LIMITED, message: msg || 'Too many requests' };
  }
  if (httpStatus === 401 || httpStatus === 403) {
    return { maCode: MA_AUTH_CODES.UNAUTHORIZED, message: msg || 'Authentication required' };
  }
  if (msgLower.includes('origin not allowed')) {
    return { maCode: MA_AUTH_CODES.UNAUTHORIZED, message: msg || 'Origin not allowed' };
  }
  if (msgLower.includes('invalid signature')) {
    return { maCode: MA_AUTH_CODES.INVALID_SIGNATURE, message: msg || 'Invalid signature' };
  }
  if (msgLower.includes('expired') || msgLower.includes('unauthorized') || msgLower.includes('not authenticated')) {
    return { maCode: MA_AUTH_CODES.SESSION_EXPIRED, message: msg || 'Session expired' };
  }
  if (msgLower.includes('invalid pair') || msgLower.includes('invalid asset')) {
    return { maCode: MA_TRADE_CODES.INVALID_PAIR, message: msg || 'Invalid pair' };
  }
  if (msgLower.includes('queue full') || msgLower.includes('queue is full')) {
    return { maCode: MA_STRAT_CODES.QUEUE_FULL, message: msg || 'Queue full' };
  }
  if (msgLower.includes('strategy not found') || msgLower.includes('not found')) {
    return { maCode: MA_STRAT_CODES.NOT_FOUND, message: msg || 'Not found' };
  }
  if (msgLower.includes('service temporarily unavailable') || msgLower.includes('service unavailable')) {
    return { maCode: 'MA-CONN-1008' as MaErrorCode, message: msg || 'Service unavailable' };
  }
  if (msgLower.includes('requests paused after a connection failure')) {
    return { maCode: 'MA-CONN-1010' as MaErrorCode, message: msg || 'Service unavailable' };
  }
  if (msgLower.includes('failed to fetch') || msgLower.includes('network error')) {
    return { maCode: 'MA-CONN-1006' as MaErrorCode, message: msg || 'Network error' };
  }
  if (msgLower.includes('timed out') || msgLower.includes('timeout')) {
    return { maCode: 'MA-CONN-1003' as MaErrorCode, message: msg || 'Request timed out' };
  }
  if (httpStatus && httpStatus >= 500) {
    return { maCode: MA_SVC_CODES.UNAVAILABLE, message: msg || 'Server error' };
  }
  return { maCode: MA_SVC_CODES.UNKNOWN, message: msg || 'Request failed' };
}

export function resolveMaErrorFromThrowable(err: unknown, fallback?: string): { maCode: MaErrorCode; message: string } {
  const e = err as { message?: string; name?: string } | null;
  const msg = String(e?.message || fallback || 'Something went wrong').trim();
  const msgLower = msg.toLowerCase();

  if (e?.name === 'ChunkLoadError' || msgLower.includes('dynamically imported module') || msgLower.includes('loading chunk')) {
    return { maCode: MA_UI_CODES.CHUNK_LOAD_FAILED, message: msg };
  }
  if (e?.name === 'MiniappApiDeferredError' || msgLower.includes('api requests deferred')) {
    return { maCode: 'MA-CONN-1010' as MaErrorCode, message: msg };
  }
  if (msgLower.includes('failed to fetch') || msgLower.includes('network error')) {
    return { maCode: 'MA-CONN-1006' as MaErrorCode, message: msg };
  }
  if (msgLower.includes('abort') || msgLower.includes('timed out')) {
    return { maCode: 'MA-CONN-1003' as MaErrorCode, message: msg };
  }
  return { maCode: MA_UI_CODES.UNKNOWN, message: msg };
}

export function resolveMaErrorFromMessage(message: string): MaErrorCode {
  return resolveMaErrorFromApi({ error: message }).maCode;
}
